import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import BillingSection from "../../components/BillingSection/BillingSection";
import CompanyAvatar from "../../components/CompanyAvatar";
import CompanyIntentFields from "../../components/CompanyIntentFields/CompanyIntentFields";
import DashboardLayout from "../../components/DashboardLayout/DashboardLayout";
import ChangePasswordModal from "../../components/Modal/ChangePasswordModal";
import TwoFactorModal from "../../components/Modal/TwoFactorModal";
import { type CompanyProfileUpdateRequest, unwrapPaginated, uploadFile } from "../../config/api";
import { uploadAccept, validateUploadFile } from "../../config/uploads";
import { getApiErrorMessage, getApiFieldErrors } from "../../lib/apiError";
import {
  type CompanyIntentErrors,
  EMPTY_INTENT,
  intentFromCompany,
  intentPayload,
  pickIntentErrors,
  validateIntent,
} from "../../lib/companyIntent";
import { getUsersProfileRetrieveQueryKey, useUsersProfileRetrieve } from "../../services/auth/generated/users/users";
import {
  useCompaniesMyCompaniesRetrieve,
  useCompaniesUpdateProfileUpdate,
} from "../../services/companies/generated/companies/companies";
import type { Company } from "../../services/companies/generated/model";
import "./ProfilePage.css";

type NotificationLabelId = "newMatches" | "complianceAlerts" | "marketIntelligence" | "messages" | "weeklySummary";

const NOTIFICATION_ITEMS: Array<{
  key: keyof CompanyProfileUpdateRequest;
  labelId: NotificationLabelId;
}> = [
  { key: "newMatchs", labelId: "newMatches" },
  { key: "ComplianceAlerts", labelId: "complianceAlerts" },
  { key: "MarketInteligenceUpdates", labelId: "marketIntelligence" },
  { key: "MessageNotifications", labelId: "messages" },
  { key: "weeklySummaryEmail", labelId: "weeklySummary" },
];

const ProfilePage = () => {
  const { t } = useTranslation(["company", "common"]);
  const queryClient = useQueryClient();

  const [saveError, setSaveError] = useState<string | null>(null);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isTwoFactorOpen, setIsTwoFactorOpen] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  // Corridor and intent (WP1.5) — sent with the main profile save.
  const [intent, setIntent] = useState(EMPTY_INTENT);
  const [intentErrors, setIntentErrors] = useState<CompanyIntentErrors>({});
  const [form, setForm] = useState<CompanyProfileUpdateRequest>({
    companyName: "",
    companyIndustry: "",
    companyDescription: "",
    companyAvatar: "",
    newMatchs: true,
    ComplianceAlerts: true,
    MarketInteligenceUpdates: true,
    MessageNotifications: true,
    weeklySummaryEmail: true,
    primaryMarket: "",
    language: "en",
    timeZone: "UTC",
  });

  // Fetch company data using Orval-generated hook
  const { data: companiesData, isLoading: loading, error: queryError } = useCompaniesMyCompaniesRetrieve();

  // The 2FA flag lives on the user profile (not the company). Pulled
  // separately so the security card can render the correct status
  // without coupling user-account state to company state.
  const { data: userProfile } = useUsersProfileRetrieve();
  const twoFactorEnabled = Boolean(userProfile?.twoFactorEnabled);

  // Backend returns a paginated wrapper; the OpenAPI schema currently lies
  // and types this as `Company[]`. unwrapPaginated handles either shape.
  const profile = unwrapPaginated<Company>(companiesData)[0] || null;
  const loadError = queryError ? t("profile.errors.loadFailed") : null;

  // Update profile mutation
  const updateProfileMutation = useCompaniesUpdateProfileUpdate();

  // Initialize form when profile data loads
  useEffect(() => {
    if (profile) {
      setForm({
        companyName: profile.companyName ?? "",
        companyIndustry: profile.companyIndustry ?? "",
        companyDescription: profile.companyDescription ?? "",
        companyAvatar: profile.companyAvatar ?? "",
        newMatchs: (profile as any).newMatchs ?? true,
        ComplianceAlerts: (profile as any).ComplianceAlerts ?? true,
        MarketInteligenceUpdates: (profile as any).MarketInteligenceUpdates ?? true,
        MessageNotifications: (profile as any).MessageNotifications ?? true,
        weeklySummaryEmail: (profile as any).weeklySummaryEmail ?? true,
        primaryMarket: profile.primaryMarket ?? "",
        language: (profile as any).language ?? "en",
        timeZone: (profile as any).timeZone ?? "UTC",
      });
      setIntent(intentFromCompany(profile));
    }
  }, [profile]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleToggle = (key: keyof CompanyProfileUpdateRequest, value: boolean) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const persistAvatar = (avatarUrl: string) => {
    updateProfileMutation.mutate(
      { data: { ...form, companyAvatar: avatarUrl } },
      {
        onSuccess: () => {
          setForm((prev) => ({ ...prev, companyAvatar: avatarUrl }));
          queryClient.invalidateQueries({
            queryKey: ["/api/v1/companies/myCompanies/"],
          });
        },
        onError: (err) => {
          setAvatarError(getApiErrorMessage(err, t("profile.errors.avatarSaveFailed")));
        },
      },
    );
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarError(null);

    const invalid = validateUploadFile(file, "image");
    if (invalid) {
      setAvatarError(invalid);
      e.target.value = "";
      return;
    }

    setIsUploadingAvatar(true);
    try {
      const res = await uploadFile(file);
      persistAvatar(res.url);
    } catch (err) {
      setAvatarError(getApiErrorMessage(err, t("profile.errors.uploadFailed")));
    } finally {
      setIsUploadingAvatar(false);
      e.target.value = "";
    }
  };

  const handleAvatarRemove = () => {
    setAvatarError(null);
    persistAvatar("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);

    const nextIntentErrors = validateIntent(intent, { required: false });
    setIntentErrors(nextIntentErrors);
    if (Object.keys(nextIntentErrors).length > 0) {
      setSaveError(t("profile.errors.fixTradeProfile"));
      return;
    }

    updateProfileMutation.mutate(
      { data: { ...form, ...intentPayload(intent) } },
      {
        onSuccess: () => {
          setIntentErrors({});
          // Invalidate queries to refresh company data
          queryClient.invalidateQueries({
            queryKey: ["/api/v1/companies/myCompanies/"],
          });
          setSaveError(null);
        },
        onError: (err) => {
          setIntentErrors(pickIntentErrors(getApiFieldErrors(err)));
          setSaveError(getApiErrorMessage(err, t("profile.errors.updateFailed")));
        },
      },
    );
  };

  return (
    <DashboardLayout>
      <div className="profile-page">
        <header className="profile-page__header">
          <div>
            <h1 className="dashboard__title">{t("profile.title")}</h1>
            <p className="dashboard__subtitle">{t("profile.subtitle")}</p>
          </div>
        </header>

        {loadError && (
          <div className="profile-page__error" role="alert">
            {loadError}
            <button
              type="button"
              className="profile-page__retry"
              onClick={() => {
                queryClient.invalidateQueries({
                  queryKey: ["/api/v1/companies/myCompanies/"],
                });
              }}
            >
              {t("common:actions.retry")}
            </button>
          </div>
        )}

        {profile && !profile.intentProfileComplete && (
          <div className="profile-page__intent-prompt" role="status">
            <i className="fa-solid fa-circle-info" aria-hidden />
            <div>
              <Trans
                t={t}
                i18nKey="profile.intentPrompt"
                components={{
                  strong: <strong />,
                  link: <a href="#trade-profile" className="profile-page__intent-prompt-link" />,
                }}
              />
            </div>
          </div>
        )}

        {loading ? (
          <div className="profile-page__loading">
            <i className="fa-solid fa-spinner fa-spin" aria-hidden />
            <span>{t("profile.loading")}</span>
          </div>
        ) : (
          <form className="profile-page__form" onSubmit={handleSubmit}>
            <section className="profile-page__card">
              <h2 className="profile-page__card-title">
                <i className="fa-regular fa-user profile-page__card-icon profile-page__card-icon--blue" />
                {t("profile.settings.title")}
              </h2>

              <div className="profile-page__avatar-row">
                <CompanyAvatar src={form.companyAvatar} name={form.companyName} size="xl" />
                <div className="profile-page__avatar-controls">
                  <span className="profile-page__avatar-label">{t("profile.settings.logo")}</span>
                  <span className="profile-page__avatar-hint">{t("profile.settings.logoHint")}</span>
                  <div className="profile-page__avatar-buttons">
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept={uploadAccept("image")}
                      className="profile-page__avatar-file"
                      onChange={handleAvatarChange}
                      disabled={isUploadingAvatar || updateProfileMutation.isPending}
                      aria-label={t("profile.settings.uploadLogo")}
                    />
                    <button
                      type="button"
                      className="profile-page__avatar-button"
                      onClick={() => avatarInputRef.current?.click()}
                      disabled={isUploadingAvatar || updateProfileMutation.isPending}
                    >
                      {isUploadingAvatar ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin" />
                          {t("common:actions.uploading")}
                        </>
                      ) : form.companyAvatar ? (
                        t("profile.settings.replace")
                      ) : (
                        t("common:actions.upload")
                      )}
                    </button>
                    {form.companyAvatar && (
                      <button
                        type="button"
                        className="profile-page__avatar-button profile-page__avatar-button--ghost"
                        onClick={handleAvatarRemove}
                        disabled={isUploadingAvatar || updateProfileMutation.isPending}
                      >
                        {t("common:actions.remove")}
                      </button>
                    )}
                  </div>
                  {avatarError && (
                    <p className="profile-page__avatar-error" role="alert">
                      {avatarError}
                    </p>
                  )}
                </div>
              </div>

              <div className="profile-page__row profile-page__row--two">
                <div className="profile-page__field">
                  <label className="profile-page__label" htmlFor="companyName">
                    {t("profile.settings.companyName")}
                  </label>
                  <input
                    id="companyName"
                    name="companyName"
                    type="text"
                    className="profile-page__input"
                    value={form.companyName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="profile-page__field">
                  <label className="profile-page__label" htmlFor="companyIndustry">
                    {t("profile.settings.industry")}
                  </label>
                  <input
                    id="companyIndustry"
                    name="companyIndustry"
                    type="text"
                    className="profile-page__input"
                    value={form.companyIndustry}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="profile-page__field">
                <label className="profile-page__label" htmlFor="companyEmail">
                  {t("profile.settings.email")}
                </label>
                <input
                  id="companyEmail"
                  type="text"
                  className="profile-page__input profile-page__input--readonly"
                  value={profile?.companyEmail ?? ""}
                  readOnly
                />
              </div>
              <div className="profile-page__field">
                <label className="profile-page__label" htmlFor="companyDescription">
                  {t("profile.settings.description")}
                </label>
                <textarea
                  id="companyDescription"
                  name="companyDescription"
                  className="profile-page__textarea"
                  rows={4}
                  value={form.companyDescription}
                  onChange={handleInputChange}
                  placeholder={t("profile.settings.descriptionPlaceholder")}
                />
              </div>
              <div className="profile-page__actions-top">
                <button type="submit" className="profile-page__save-btn" disabled={updateProfileMutation.isPending}>
                  {updateProfileMutation.isPending ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin" />
                      {t("profile.saving")}
                    </>
                  ) : (
                    t("profile.saveChanges")
                  )}
                </button>
              </div>
            </section>

            <section className="profile-page__card" id="trade-profile">
              <h2 className="profile-page__card-title">
                <i className="fa-solid fa-route profile-page__card-icon profile-page__card-icon--blue" />
                {t("profile.trade.title")}
              </h2>
              <CompanyIntentFields
                idPrefix="profile-intent"
                value={intent}
                onChange={setIntent}
                errors={intentErrors}
                disabled={updateProfileMutation.isPending}
              />
              <div className="profile-page__actions-top">
                <button type="submit" className="profile-page__save-btn" disabled={updateProfileMutation.isPending}>
                  {updateProfileMutation.isPending ? t("profile.saving") : t("profile.trade.save")}
                </button>
              </div>
            </section>

            <section className="profile-page__card">
              <h2 className="profile-page__card-title">
                <i className="fa-regular fa-bell profile-page__card-icon profile-page__card-icon--orange" />
                {t("profile.notifications.title")}
              </h2>
              {NOTIFICATION_ITEMS.map(({ key, labelId }) => (
                <div key={key} className="profile-page__notif-row">
                  <div className="profile-page__notif-text">
                    <span className="profile-page__notif-title">
                      {t(`profile.notifications.items.${labelId}.title`)}
                    </span>
                    <span className="profile-page__notif-desc">
                      {t(`profile.notifications.items.${labelId}.description`)}
                    </span>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={Boolean(form[key])}
                    className={`profile-page__toggle ${form[key] ? "profile-page__toggle--on" : ""}`}
                    onClick={() => handleToggle(key, !form[key])}
                  >
                    <span className="profile-page__toggle-slider" />
                  </button>
                </div>
              ))}
            </section>

            <section className="profile-page__card">
              <h2 className="profile-page__card-title">
                <i className="fa-solid fa-lock profile-page__card-icon profile-page__card-icon--red" />
                {t("profile.security.title")}
              </h2>
              <button
                type="button"
                className="profile-page__security-row profile-page__security-row--button"
                onClick={() => setIsChangePasswordOpen(true)}
              >
                <div>
                  <span className="profile-page__security-title">{t("profile.security.changePassword")}</span>
                  <span className="profile-page__security-desc">{t("profile.security.changePasswordDesc")}</span>
                </div>
                <i className="fa-solid fa-chevron-right profile-page__arrow" aria-hidden />
              </button>
              <button
                type="button"
                className="profile-page__security-row profile-page__security-row--button"
                onClick={() => setIsTwoFactorOpen(true)}
              >
                <div>
                  <span className="profile-page__security-title">
                    {t("profile.security.twoFactor")}
                    {!twoFactorEnabled && (
                      <span className="profile-page__badge">{t("profile.security.recommended")}</span>
                    )}
                  </span>
                  <span className="profile-page__security-desc">
                    {twoFactorEnabled
                      ? t("profile.security.twoFactorEnabled")
                      : t("profile.security.twoFactorDisabled")}
                  </span>
                </div>
                <i className="fa-solid fa-chevron-right profile-page__arrow" aria-hidden />
              </button>
              <div className="profile-page__security-row">
                <div>
                  <span className="profile-page__security-title">{t("profile.security.activeSessions")}</span>
                  <span className="profile-page__security-desc">
                    {t("profile.security.activeSessionsDesc", { count: 2 })}
                  </span>
                </div>
                <i className="fa-solid fa-chevron-right profile-page__arrow" aria-hidden />
              </div>
            </section>

            <section className="profile-page__card">
              <h2 className="profile-page__card-title">
                <i className="fa-solid fa-globe profile-page__card-icon profile-page__card-icon--green" />
                {t("profile.regional.title")}
              </h2>
              <div className="profile-page__field">
                <label className="profile-page__label" htmlFor="primaryMarket">
                  {t("profile.regional.primaryMarket")}
                </label>
                <input
                  id="primaryMarket"
                  name="primaryMarket"
                  type="text"
                  className="profile-page__input"
                  placeholder={t("profile.regional.primaryMarketPlaceholder")}
                  value={form.primaryMarket}
                  onChange={handleInputChange}
                />
              </div>
              <div className="profile-page__row profile-page__row--two">
                <div className="profile-page__field">
                  <label className="profile-page__label" htmlFor="language">
                    {t("common:language.label")}
                  </label>
                  <select
                    id="language"
                    name="language"
                    className="profile-page__select"
                    value={form.language}
                    onChange={handleInputChange}
                  >
                    <option value="en">{t("profile.regional.languages.en")}</option>
                    <option value="ar">{t("profile.regional.languages.ar")}</option>
                    <option value="fa">{t("profile.regional.languages.fa")}</option>
                  </select>
                </div>
                <div className="profile-page__field">
                  <label className="profile-page__label" htmlFor="timeZone">
                    {t("profile.regional.timeZone")}
                  </label>
                  <input
                    id="timeZone"
                    name="timeZone"
                    type="text"
                    className="profile-page__input"
                    placeholder={t("profile.regional.timeZonePlaceholder")}
                    value={form.timeZone}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </section>

            <BillingSection />

            {saveError && (
              <div className="profile-page__save-error" role="alert">
                {saveError}
              </div>
            )}
          </form>
        )}
      </div>

      <ChangePasswordModal isOpen={isChangePasswordOpen} onClose={() => setIsChangePasswordOpen(false)} />
      <TwoFactorModal
        isOpen={isTwoFactorOpen}
        enabled={twoFactorEnabled}
        onMutated={() => {
          // Refresh the cached user profile so the row's enabled/disabled
          // copy reflects the new state immediately.
          queryClient.invalidateQueries({ queryKey: getUsersProfileRetrieveQueryKey() });
        }}
        onClose={() => setIsTwoFactorOpen(false)}
      />
    </DashboardLayout>
  );
};

export default ProfilePage;
